
import React from 'react';
import { Eye, EyeOff, LogIn, Mail, Key } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from './FormField';
import { AuthButton } from './AuthButton';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface SignInTabProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  showPassword: boolean;
  handlePasswordVisibility: () => void;
  rememberMe: boolean;
  setRememberMe: (rememberMe: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  networkError: string | null;
  setNetworkError: (error: string | null) => void;
  resetErrors: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const SignInTab = ({ 
  email, 
  setEmail, 
  password, 
  setPassword, 
  showPassword, 
  handlePasswordVisibility,
  rememberMe,
  setRememberMe,
  loading,
  setLoading,
  networkError,
  setNetworkError,
  resetErrors,
  onKeyDown
}: SignInTabProps) => {
  const { signIn } = useAuth();
  const { toast } = useToast();

  const handleSignIn = async () => {
    if (!email || !password) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    resetErrors();
    
    try {
      const result = await signIn(email, password, rememberMe);
      
      if (result.error) {
        if (result.error.message?.includes('connexion réseau') || 
            result.error.message === 'Failed to fetch') {
          setNetworkError('Problème de connexion internet. Veuillez réessayer.');
          toast({
            title: 'Erreur de connexion',
            description: 'Problème de connexion internet. Veuillez réessayer.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erreur',
            description: result.error.message || 'Une erreur est survenue',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabsContent value="signin" className="animate-fade-in">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-gray-800 dark:text-white">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-youtube-red to-youtube-red-dark">Bienvenue</span>
        </CardTitle>
        <CardDescription className="text-gray-500 dark:text-gray-400">Connectez-vous à votre compte</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <FormField
          id="email"
          type="email"
          placeholder="email@exemple.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          icon={<Mail className="w-4 h-4 mr-2 text-youtube-red-light" />}
          label="Email"
        />
        <FormField
          id="password"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          icon={<Key className="w-4 h-4 mr-2 text-youtube-red-light" />}
          label="Mot de passe"
          rightIcon={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          onClick={handlePasswordVisibility}
        />
        <div className="flex items-center space-x-2 mt-2 select-none">
          <Checkbox 
            id="remember-me" 
            checked={rememberMe} 
            onCheckedChange={(checked) => setRememberMe(checked === true)}
            className="text-youtube-red border-gray-300 dark:border-gray-600 data-[state=checked]:bg-youtube-red data-[state=checked]:border-youtube-red"
          />
          <label
            htmlFor="remember-me"
            className="text-sm font-medium leading-none text-gray-600 dark:text-gray-400 cursor-pointer hover:text-youtube-red transition-colors duration-300"
          >
            Se souvenir de moi
          </label>
        </div>
        {networkError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-md text-sm text-red-800 dark:text-red-400 animate-fade-in">
            {networkError}
          </div>
        )}
      </CardContent>
      <CardFooter className="pb-6">
        <AuthButton
          id="signin-button"
          onClick={handleSignIn}
          loading={loading}
          icon={<LogIn className="w-5 h-5" />}
          text="Se connecter"
          loadingText="Connexion..."
        />
      </CardFooter>
    </TabsContent>
  );
};
